import { useRef, useCallback, useEffect } from 'react'

// ============================================
// RangeSliderControl — TWO-handle fader, HORIZONTAL.
//
// Visual language lifted from WalletHoldings' SliderControl (same groove,
// tick ladder, ribbed cap, and geometry constants) so the heat sensitivity
// control reads as the same instrument family as the trade station's faders.
// Two differences from the station's faders: the payload (one track, two
// caps, and the FILL is the band BETWEEN them — literally what the setting
// means: show liquidity from `lo` up to `hi` of the depth range), and the
// orientation — the heat menu is a wide panel, so this runs left-to-right
// with the caps rotated to match. Min at the left, max at the right.
//
// Interaction:
//   • drag either cap; grabbing the track moves whichever cap is nearer
//   • the caps cannot cross (each clamps against the other)
//   • wheel over the right half steps `hi`, left half steps `lo`
//   • non-passive wheel listener so the menu never scrolls underneath
// ============================================

const TRACK_W = 4
// Cap size matched to WalletHoldings' SliderControl (capW = max(26, size*
// 0.62) -> 32 at the default size 52; capH 26) so the two controls read as
// the same hardware. The travel math insets by CAP_W/2, so the caps always
// sit fully inside the groove regardless of this number.
const CAP_W = 32
const CAP_H = 26

function Ribs({ color }) {
  // Seven divisions instead of five, and the lit centre rib runs at a third
  // of a normal rib's width — the old 1-of-5 highlight was a broad white
  // band across the cap. Narrow highlight, tighter glow: it reads as a
  // machined centre line instead of a stripe.
  const ribs = []
  for (let i = 0; i < 7; i++) {
    const t = i / 6
    const mid = i === 3
    const g = Math.round(18 + 30 * Math.abs(0.5 - t) * 2)
    ribs.push(
      <div key={i} style={{
        flex: mid ? 0.34 : 1,
        background: mid ? '#9a9aa2' : `rgb(${g},${g},${g + 4})`,
        boxShadow: mid ? `0 0 2px ${color}` : 'none',
      }} />
    )
  }
  return <>{ribs}</>
}

export default function RangeSliderControl({
  lo, hi, onChange,          // onChange(nextLo, nextHi)
  min = 0, max = 1, step = 0.005,
  label = '',
  colorLo = '#3b6ea5',
  colorHi = '#5fa8ff',
  width = 200,
  single = false,            // one cap: lo pinned at min, value = hi
  format = null,
}) {
  const trackRef = useRef(null)
  const dragging = useRef(null)      // 'lo' | 'hi' | null

  const trackLen = width
  const span = max > min ? max - min : 1

  // The values live at the caps' INNER TIPS, not their centres — the lower
  // cap's RIGHT edge marks `lo`, the upper cap's LEFT edge marks `hi`. This
  // is the honest model for a range control (the band is what's between the
  // grips) and it dissolves the minimum-width problem the centre model
  // created: tips can meet at zero band while the caps merely touch, since
  // each cap's body sits OUTSIDE the range it bounds. The value axis is
  // therefore inset by a full cap on each side.
  const axisL = CAP_W
  const axisLen = Math.max(1, trackLen - 2 * CAP_W)
  const xOf = (v) => {
    const p = Math.max(0, Math.min(1, (v - min) / span))
    return axisL + p * axisLen
  }
  const valueFromClientX = useCallback((clientX) => {
    const el = trackRef.current
    if (!el) return null
    const r = el.getBoundingClientRect()
    const len = Math.max(1, r.width - 2 * CAP_W)
    const p = Math.max(0, Math.min(1, (clientX - r.left - CAP_W) / len))
    const raw = min + p * span
    return Math.max(min, Math.min(max, Math.round(raw / step) * step))
  }, [min, max, span, step])

  // BUMPER CARS. A hard clamp made the dragged cap stop dead against its
  // neighbour, which feels broken when you're trying to move the whole band
  // to one end. Instead the moving cap PUSHES the other ahead of it, and the
  // pair travels together until the pushed one hits the wall — at which
  // point the dragged one stops too, because the invariants still hold:
  //   min <= lo,  lo + GAP <= hi,  hi <= max
  // GAP is one step, so the two can never cross or coincide.
  // One step is enough now. With the caps mounted OUTSIDE the range they
  // bound, lo and hi being one step apart puts their tips ~a pixel apart and
  // their bodies still fully clear of each other — no pixel-space fudge
  // needed, and no artificial floor on how narrow the band can be.
  const GAP = step
  const apply = useCallback((which, v) => {
    if (single) { onChange(min, Math.max(min, Math.min(max, v))); return }
    if (which === 'lo') {
      let nLo = Math.max(min, Math.min(v, max - GAP))
      let nHi = hi
      if (nLo > nHi - GAP) {            // pushing the upper cap
        nHi = Math.min(max, nLo + GAP)
        nLo = Math.min(nLo, nHi - GAP)  // upper hit the wall -> lower stops
      }
      onChange(nLo, nHi)
    } else {
      let nHi = Math.min(max, Math.max(v, min + GAP))
      let nLo = lo
      if (nHi < nLo + GAP) {            // pushing the lower cap
        nLo = Math.max(min, nHi - GAP)
        nHi = Math.max(nHi, nLo + GAP)  // lower hit the wall -> upper stops
      }
      onChange(nLo, nHi)
    }
  }, [lo, hi, step, min, max, GAP, onChange, single])

  const handlePointerDown = (e) => {
    const v = valueFromClientX(e.clientX)
    if (v == null) return
    // Grab the nearer cap — the natural expectation when clicking the track.
    dragging.current = single ? 'hi' : (Math.abs(v - lo) <= Math.abs(v - hi) ? 'lo' : 'hi')
    trackRef.current?.setPointerCapture?.(e.pointerId)
    apply(dragging.current, v)
  }
  const handlePointerMove = (e) => {
    if (!dragging.current) return
    const v = valueFromClientX(e.clientX)
    if (v != null) apply(dragging.current, v)
  }
  const handlePointerUp = (e) => {
    dragging.current = null
    trackRef.current?.releasePointerCapture?.(e.pointerId)
  }

  // Wheel: upper half of the track steps `hi`, lower half steps `lo`.
  useEffect(() => {
    const el = trackRef.current
    if (!el) return
    const handler = (e) => {
      e.preventDefault()
      const r = el.getBoundingClientRect()
      const which = single ? 'hi' : ((e.clientX - r.left) < r.width / 2 ? 'lo' : 'hi')
      const dir = e.deltaY < 0 ? 1 : -1
      apply(which, (which === 'lo' ? lo : hi) + dir * step)
    }
    el.addEventListener('wheel', handler, { passive: false })
    return () => el.removeEventListener('wheel', handler)
  }, [apply, lo, hi, step, single])

  const xLo = xOf(lo), xHi = xOf(hi)
  const fmt = format || ((v) => `${Math.round(v * 100)}%`)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 3, width }}>
      {label && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontSize: 8, fontWeight: 700, color: '#555', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {label}
          </span>
          <span style={{ fontSize: 9, fontWeight: 700, fontFamily: 'monospace' }}>
            {single ? <span style={{ color: colorHi }}>{fmt(hi)}</span> : (<>
            <span style={{ color: colorLo }}>{fmt(lo)}</span>
            <span style={{ color: '#555' }}> · </span>
            <span style={{ color: colorHi }}>{fmt(hi)}</span>
            </>)}
          </span>
        </div>
      )}
      <div
        ref={trackRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
        style={{
          position: 'relative',
          width: trackLen,
          height: CAP_H + 6,
          cursor: 'pointer',
          touchAction: 'none',
          // The "no-go" cursor mid-drag was the browser kicking off a NATIVE
          // element drag on top of our pointer capture — it paints
          // not-allowed because there's no valid drop target. Refusing
          // dragstart and disabling selection removes that layer entirely.
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
        }}
      >
        {/* Tick ladder — same major/minor pattern, rotated to run across. */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1 }}>
          {Array.from({ length: 11 }).map((_, i) => {
            if (i === 0 || i === 10) return null
            const major = i % 2 === 0
            const len = major ? 7 : 4
            const shade = major ? '#666' : '#4a4a52'
            const gap = TRACK_W / 2 + 3
            const majorLen = 7
            return (
              <div key={i} style={{ position: 'absolute', left: axisL + (i / 10) * axisLen, top: 0, bottom: 0, width: 1, transform: 'translateX(-50%)' }}>
                <div style={{ position: 'absolute', bottom: `calc(50% + ${gap}px + ${majorLen - len}px)`, height: len, width: 1, background: shade }} />
                <div style={{ position: 'absolute', top: `calc(50% + ${gap}px + ${majorLen - len}px)`, height: len, width: 1, background: shade }} />
              </div>
            )
          })}
        </div>

        {/* Groove */}
        <div style={{
          position: 'absolute', top: '50%', left: 0, transform: 'translateY(-50%)',
          height: TRACK_W, width: trackLen, background: '#15151b',
          borderRadius: TRACK_W / 2, zIndex: 0,
        }} />

        {/* Fill = the BAND between the TIPS — exactly the span the two grips
            enclose, which is what the setting means. */}
        <div style={{
          position: 'absolute', top: '50%', transform: 'translateY(-50%)',
          left: xLo, width: Math.max(0, xHi - xLo),
          height: TRACK_W,
          background: `linear-gradient(to right, ${colorLo}, ${colorHi})`,
          borderRadius: TRACK_W / 2, zIndex: 2,
        }} />

        {/* Caps */}
        {(single ? [['hi', xHi, colorHi, 1]] : [['lo', xLo, colorLo, -1], ['hi', xHi, colorHi, 1]]).map(([which, x, col, side]) => (
          <div key={which} style={{
            // side -1: cap sits to the LEFT of its value (right edge = tip)
            // side +1: cap sits to the RIGHT of its value (left edge = tip)
            position: 'absolute', left: x + side * (CAP_W / 2), top: '50%',
            transform: 'translate(-50%, -50%)',
            width: CAP_W, height: CAP_H,
            display: 'flex', flexDirection: 'row',
            borderRadius: 3, overflow: 'hidden',
            border: '1px solid #000',
            boxShadow: '0 2px 5px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(255,255,255,0.06)',
            zIndex: 3,
            pointerEvents: 'none',   // the track owns the gesture
          }}>
            <Ribs color={col} />
          </div>
        ))}
      </div>
    </div>
  )
}